import translate from '@translate';
import React, { FC, useRef, useState, useEffect } from 'react';
import Button from 'src/components/Button';
import FormBlock from 'src/components/FormBlock';
import Grid, { GridItem } from 'src/components/Grid';
import Select from 'src/components/Select';
import TextField from 'src/components/TextField';
import Typography from 'src/components/Typography';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { SystemConfigStructureItemType } from 'src/types/systemConfigDetail';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse/Collapse';
import { checkFormValidity, EditDataType, getEditData } from 'src/utils/formFunctions';
import { FormErrorsType, OnChangeType } from '../../types/global';

export const EDIT_STRUCTURE_KEYS = ['ITEM_KEY', 'TYPE', 'DESCRIPTION'];
const initStructureData = {};

export type EditStructureKeysType = 'ITEM_KEY' | 'TYPE' | 'DESCRIPTION';

export interface StructureItemBlockPropsType {
    editData?: SystemConfigStructureItemType;
    permissionUpdateStructure: boolean;
    configItemTypeCodes: ConstantsCodeType[];
    handleSubmit: (editData: EditDataType) => void;
    handleAddMessage: (message: any) => void;
}

const StructureItemBlock: FC<StructureItemBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [open, setOpen] = useState<boolean>(false);
    const [editData, setEditData] = useState<EditDataType>(getEditData(initStructureData || {}, EDIT_STRUCTURE_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    const colapseIcon = open ? <ExpandLess /> : <ExpandMore />;

    useEffect(() => {
        setEditData(props.editData || {});
        if (props.editData) {
            setOpen(true);
        }
        setEditErrors({});
    }, [props.editData]);

    const onChange: OnChangeType<EditStructureKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSubmit = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            props.handleSubmit(editData);
        } else {
            props.handleAddMessage({
                type: 'warning',
                title: translate('messages.formWarningTitle'),
                text: translate('messages.formWarningText'),
            });
        }

        setEditErrors(newFormErrors);
    };

    return (
        <FormBlock>
            <Typography>
                {translate(editData.ID ? 'systemConfig.editStructureItem' : 'systemConfig.newStructureItem')}
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        setOpen(!open);
                    }}
                >
                    {colapseIcon}
                </Button>
            </Typography>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <form ref={formRef}>
                    <Grid>
                        <GridItem cols={4}>
                            <TextField
                                value={editData.ITEM_KEY}
                                error={editErrors.ITEM_KEY}
                                onChange={(value, error) => onChange('ITEM_KEY', value, error)}
                                name="ITEM_KEY"
                                label={translate('systemConfig.structure.key')}
                                fullWidth
                                required
                                disabled={!props.permissionUpdateStructure}
                            />
                        </GridItem>
                        <GridItem cols={4}>
                            <Select
                                options={props.configItemTypeCodes}
                                value={editData.TYPE}
                                error={editErrors.TYPE}
                                onChange={(value, error) => onChange('TYPE', value, error)}
                                name="TYPE"
                                label={translate('systemConfig.structure.type')}
                                fullWidth
                                required
                                disabled={!props.permissionUpdateStructure}
                            />
                        </GridItem>
                        <GridItem cols={4}>
                            <TextField
                                value={editData.DESCRIPTION}
                                error={editErrors.DESCRIPTION}
                                onChange={(value, error) => onChange('DESCRIPTION', value, error)}
                                name="DESCRIPTION"
                                label={translate('systemConfig.structure.description')}
                                fullWidth
                                multiline
                                disabled={!props.permissionUpdateStructure}
                            />
                        </GridItem>
                        <GridItem cols={4}>
                            <Button onClick={handleSubmit} mobileFullWidht>
                                {translate(
                                    editData.ID
                                        ? 'systemConfig.action.saveStuctureItem'
                                        : 'systemConfig.action.addStuctureItem'
                                )}
                            </Button>
                        </GridItem>
                    </Grid>
                </form>
            </Collapse>
        </FormBlock>
    );
};

export default StructureItemBlock;
